import {useLocation, useParams} from "react-router";
import type {GroupRepo, RecipeEntry, RecipeId} from "../../models/GroupRepo";
import {useSortedIngredients} from "../../models/recipe-queries";
import Markdown from "../../shared/markdown/Markdown";
import WakeLock from "../../shared/WakeLock";
import {Col, css, Row, Text} from "../../ui/Box";
import Button from "../../ui/Button";
import IngredientPill from "./IngredientPill";

const InnerInspect = ({
  recipe,
  groupRepo,
  count,
}: {
  groupRepo: GroupRepo;
  recipe: RecipeEntry;
  count: number | null;
}) => {
  const ingredients = useSortedIngredients(groupRepo, recipe.id);
  const items = groupRepo.Items.useGetAll();

  return (
    <Col sp="24" maxWidth="formWidth" width="100%" mr="auto" ml="auto" pb="24">
      <WakeLock />
      <Row align="end">
        <h1>{recipe.name}</h1>
        <Button className={css({ml: "auto"})} to="./edit">
          Edit
        </Button>
      </Row>
      {recipe.description && <Markdown>{recipe.description}</Markdown>}
      <Text>{count ?? recipe.defaultPortions} portions</Text>
      <Col sp="4">
        {ingredients.map((ingredient) => (
          <IngredientPill
            key={ingredient.id}
            item={items[ingredient.itemId]}
            ingredient={ingredient}
            ratio={count ? count / recipe.defaultPortions : 1}
          />
        ))}
      </Col>
    </Col>
  );
};

const useCountFromLocation = () => {
  const query = useLocation().search;
  if (!query) return null;
  const parsedQuery = new URLSearchParams(query);
  const count = Number(parsedQuery.get("count"));
  return count > 0 ? count : null;
};

const InspectRecipe = ({groupRepo}: {groupRepo: GroupRepo}) => {
  const params = useParams<{recipeId: RecipeId}>();
  const recipe = groupRepo.Recipes.useById(params.recipeId!);
  const count = useCountFromLocation();

  return recipe ? (
    <InnerInspect recipe={recipe} groupRepo={groupRepo} count={count} />
  ) : (
    <Col sp="24" maxWidth="formWidth" width="100%" mr="auto" ml="auto" pb="24">
      <Text size="12" color="secondary">
        No such recipe
      </Text>
    </Col>
  );
};

export default InspectRecipe;
